import {FetchBaseQueryError} from '@reduxjs/toolkit/dist/query';
import {QueryReturnValue} from '@reduxjs/toolkit/dist/query/baseQueryTypes';

import {LANE_RTK_TAGS} from 'app/lanes/api';
import {sweetAPI} from 'app/store/sweet-api';
import {
  createTagsListFromResponse,
  showLoadingBar,
  showMessages,
} from 'app/store/sweet-api/utils';
import {taskDispatcher} from 'app/subscriptions/taskDispatcher';
import {
  PROCESS_API_URL,
  PROCESS_API_URL_COPY,
  PROCESS_HASHID_API_URL,
  PROCESS_VERSION_APPROVE_API_URL,
  PROCESS_VERSIONS_API,
} from 'app/urls';
import {gt} from 'app/utils/i18n';

import {DEFAULT_PAGE_SIZE} from '../utils/constants';

import type {
  ProcessDashboardFetchParamsType,
  ProcessDashboardLocationParamsType,
} from './types';
import type {
  ListResponseType,
  ProcessHashidSerializedType,
  ProcessSerializedType,
  ProcessVersionSerializedType,
  TaskJobSerializedType,
} from 'sweet-types';

export const PROCESS_RTK_TAGS = {
  TAGS: 'PROCESS',
  HASHID: 'PROCESSHASHID',
};

export const extendedAPI = sweetAPI
  .enhanceEndpoints({
    addTagTypes: [
      PROCESS_RTK_TAGS.TAGS,
      PROCESS_RTK_TAGS.HASHID,
      LANE_RTK_TAGS.TAGS,
    ],
  })
  .injectEndpoints({
    endpoints: (build) => ({
      processByHashid: build.query<ProcessHashidSerializedType, string>({
        query: (hashid) => ({
          url: `${PROCESS_HASHID_API_URL}${hashid}/`,
        }),
        providesTags: (response) =>
          response != null
            ? [{type: PROCESS_RTK_TAGS.HASHID, id: response.url}]
            : [],

        onQueryStarted: showMessages({
          onError: () => gt('Could not fetch the process.'),
        }),
      }),
      processById: build.query<ProcessSerializedType, number>({
        query: (id) => ({
          url: `${PROCESS_API_URL}${id}/`,
        }),
        providesTags: (response) =>
          response != null
            ? [{type: PROCESS_RTK_TAGS.TAGS, id: response.url}]
            : [],
        onQueryStarted: showMessages({
          onError: () => gt('Could not fetch the process.'),
        }),
      }),
      processes: build.query<
        ListResponseType<ProcessSerializedType>,
        | {lane?: string}
        | {
            policy_id: number;
            fields: Array<string>;
          }
        | void
        | ProcessDashboardFetchParamsType
      >({
        query: (params) => ({
          url: PROCESS_API_URL,
          params: params ?? {},
        }),
        providesTags: (response) =>
          createTagsListFromResponse(PROCESS_RTK_TAGS.TAGS, response, {
            addsListId: true,
            idGetter: (process) => process.url,
          }),
        onQueryStarted: showMessages({
          onError: {
            fallback: () => gt('Could not fetch the processes.'),
            byStatus: {},
            silenceByStatus: {404: true},
          },
        }),
      }),
      processVersions: build.query<
        ListResponseType<ProcessVersionSerializedType>,
        {
          processId?: number;
          page?: string | number;
          page_size?: string;
        }
      >({
        query: ({processId, page = 1, page_size = DEFAULT_PAGE_SIZE}) => ({
          url: PROCESS_VERSIONS_API,
          params: {version_object_id: processId, page, page_size},
        }),
        providesTags: () => [{type: PROCESS_RTK_TAGS.TAGS, id: 'list'}],
        onQueryStarted: showMessages({
          onError: () => gt('Could not fetch process versions.'),
        }),
      }),
      processVersionDetail: build.query<
        ProcessVersionSerializedType,
        {versionId: string | number}
      >({
        query: ({versionId}) => `${PROCESS_VERSIONS_API}${versionId}/`,
        onQueryStarted: showMessages({
          onError: () => gt('Could not fetch process version'),
        }),
        providesTags: () => [{type: PROCESS_RTK_TAGS.TAGS, id: 'list'}],
      }),
      updateProcess: build.mutation<
        ProcessSerializedType,
        {url: string} & Partial<ProcessSerializedType> & {
            successMessage?: string;
            errorMessage?: string;
          }
      >({
        query: ({url, ...body}) => ({
          url: url,
          method: 'PATCH',
          body,
        }),
        onQueryStarted: showMessages({
          onSuccess: ({successMessage}) => successMessage ?? null,
          onError: (error, {errorMessage}) =>
            errorMessage ?? gt('Unable to update the process'),
        }),
        invalidatesTags: (result, error, {url}) => [
          {type: PROCESS_RTK_TAGS.TAGS, id: url},
          {type: PROCESS_RTK_TAGS.HASHID, id: url},
        ],
      }),

      approveProcessVersion: build.mutation<
        unknown,
        {
          documentId: number;
          versionURL: string;
          customNotificationText?: string;
          options: {notify?: boolean; signoff?: boolean};
        }
      >({
        query: ({versionURL, options}) => ({
          method: 'POST',
          url: PROCESS_VERSION_APPROVE_API_URL,
          body: {url: versionURL, ...options},
        }),
        onQueryStarted: (args, api) => {
          showMessages({
            onSuccess: ({customNotificationText, options}) =>
              customNotificationText != null
                ? customNotificationText
                : options.notify
                ? gt('Approved. Teammates will be notified')
                : gt('This version is now approved and live.'),
            onError: () => gt('Could not approve that version.'),
          })(args, api);
        },
        invalidatesTags: () => [
          {type: LANE_RTK_TAGS.TAGS, id: 'list'},
          {type: PROCESS_RTK_TAGS.TAGS, id: 'list'},
          {type: PROCESS_RTK_TAGS.HASHID, id: 'list'},
        ],
      }),
      duplicateProcess: build.mutation<
        TaskJobSerializedType,
        {
          name: string;
          url: string;
          teams?: Array<string>;
          duplicate_procedures?: boolean;
        }
      >({
        queryFn: (
          body,
          api,
          extraOptions,
          baseQuery
        ): Promise<
          QueryReturnValue<TaskJobSerializedType, FetchBaseQueryError, any>
        > => {
          return api
            .dispatch(
              taskDispatcher(
                (
                  baseQuery({
                    url: PROCESS_API_URL_COPY,
                    method: 'POST',
                    body,
                  }) as Promise<
                    QueryReturnValue<
                      TaskJobSerializedType,
                      FetchBaseQueryError,
                      any
                    >
                  >
                ).then((response) => {
                  if (response.data) {
                    return response.data;
                  } else {
                    throw response.error;
                  }
                })
              )
            )
            .then(
              (job) => {
                return {data: job};
              },
              (error) => ({error})
            );
        },
        onQueryStarted: (args, api) => {
          showLoadingBar(args, api);
          showMessages({
            onError: () => gt('Unable to duplicate'),
          })(args, api);
        },
        invalidatesTags: () => [{type: PROCESS_RTK_TAGS.TAGS, id: 'list'}],
      }),
    }),
  });

export const {
  useProcessByHashidQuery: useProcessByHashid,
  useProcessByIdQuery: useProcessById,
  useProcessesQuery: useProcesses,
  useLazyProcessesQuery: useLazyProcesses,
  useProcessVersionsQuery: useProcessVersions,
  useProcessVersionDetailQuery: useProcessVersionDetail,
  useUpdateProcessMutation: useUpdateProcess,
  useApproveProcessVersionMutation: useApproveProcessVersion,
  useDuplicateProcessMutation: useDuplicateProcess,
} = extendedAPI;

export const convertLocationParamsToFetchParams = (
  query: ProcessDashboardLocationParamsType
): ProcessDashboardFetchParamsType => ({
  page: query.page != null ? query.page : '1',
  page_size: query.page_size != null ? query.page_size : DEFAULT_PAGE_SIZE,
  search: query.search,
  ordering: query.ordering,
  team_id: query.team_id !== '-1' ? query.team_id : undefined,
  in_no_teams: query.team_id === '-1' ? '1' : undefined,
  exclude_team_id: query.exclude_team_id,
  tag: query.tag != null ? query.tag : undefined,
  visible_to_user:
    query.visible_to_user != null ? query.visible_to_user : undefined,
  filter: query.filter,
  folder: query.folder,
});
